import { hapticImpactIfMobile } from "@/client/ui/hooks/hapticImpactIfMobile";
import type { PropsOf } from "@pinyinly/lib/types";
import { useState } from "react";
import type { ReactNode } from "react";
import { Text } from "@/client/ui/Text";
import { View } from "@/client/ui/View";
import type { Pressable } from "react-native";
import Reanimated, {
  Easing,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withClamp,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { scheduleOnRN, scheduleOnUI } from "react-native-worklets";
import { tv } from "tailwind-variants";
import {
  quizAnimationDuration,
  withIncorrectShakeAnimation,
  withIncorrectWobbleAnimation,
} from "./animations";
import { ReanimatedPressable } from "./ReanimatedPressable";
import { ScopedVariables } from "./ScopedVariables";
import { ShootingStars } from "./ShootingStars";
import { textAnswerButtonFontSize } from "./TextAnswerButton.utils";
import type {
  TextAnswerButtonFontSize,
  TextAnswerButtonState,
} from "./TextAnswerButton.utils";

const targetBgScale: Record<TextAnswerButtonState, number> = {
  dimmed: 0.5,
  default: 0.5,
  selected: 1,
  error: 1,
  success: 1,
  warning: 1,
};
const targetBgOpacity: Record<TextAnswerButtonState, number> = {
  dimmed: 0,
  default: 0,
  selected: 1,
  error: 1,
  success: 1,
  warning: 1,
};
const targetScale: Record<TextAnswerButtonState, number> = {
  dimmed: 1,
  default: 1,
  selected: 1,
  error: 1,
  success: 1,
  warning: 1,
};
const targetRotation: Record<TextAnswerButtonState, string> = {
  dimmed: `0deg`,
  default: `0deg`,
  selected: `0deg`,
  error: `0deg`,
  success: `0deg`,
  warning: `0deg`,
};

export type TextAnswerButtonProps = {
  text: string;
  /**
   * Allow setting the font size explicitly, this allows making all choices in a
   * quiz the same font size which avoids subconsciously biasing answers based
   * on font size.
   */
  fontSize?: TextAnswerButtonFontSize;
  state?: TextAnswerButtonState;
  className?: string;
  renderWikiModal?: (onDismiss: () => void) => ReactNode;
  inFlexRowParent?: boolean;
  textClassName?: string;
  disabled?: boolean;
} & Omit<PropsOf<typeof Pressable>, `children` | `disabled`>;

export function TextAnswerButton({
  disabled = false,
  text,
  fontSize = textAnswerButtonFontSize(text),
  state = `default`,
  renderWikiModal,
  inFlexRowParent = false,
  className,
  textClassName,
  ...pressableProps
}: TextAnswerButtonProps) {
  const [prevState, setPrevState] = useState(state);
  const [bgFilled, setBgFilled] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [showWikiModal, setShowWikiModal] = useState(false);

  const scaleSv = useSharedValue(targetScale[state]);
  const rotationSv = useSharedValue(targetRotation[state]);
  const bgScaleSv = useSharedValue(targetBgScale[state]);
  const bgOpacitySv = useSharedValue(targetBgOpacity[state]);

  if (state !== prevState) {
    let newScale = targetScale[state];
    let newBgScale = targetBgScale[state];
    let newRotation = targetRotation[state];
    let newBgOpacity = targetBgOpacity[state];

    scheduleOnUI(() => {
      switch (state) {
        case `dimmed`:
        case `default`: {
          break;
        }
        case `selected`: {
          newBgScale = withClamp(
            { max: 1 },
            withPulseSpringAnimation(newBgScale),
          );
          newBgOpacity = withClamp(
            { min: bgOpacitySv.get() },
            withTiming(newBgOpacity, { duration: quizAnimationDuration }),
          );
          newScale = withClamp({ min: 1 }, withPulseSpringAnimation(newScale));
          break;
        }
        case `error`: {
          newBgOpacity = withClamp(
            { min: bgOpacitySv.get() },
            withTiming(newBgOpacity, { duration: quizAnimationDuration }),
          );
          newRotation = withIncorrectWobbleAnimation();
          break;
        }
        case `success`:
        case `warning`: {
          newBgOpacity = withClamp(
            { min: bgOpacitySv.get() },
            withTiming(newBgOpacity, { duration: quizAnimationDuration }),
          );
          break;
        }
      }

      scaleSv.set(newScale);
      bgScaleSv.set(newBgScale);
      bgOpacitySv.set(newBgOpacity);
      rotationSv.set(newRotation);
    });

    setPrevState(state);
  }

  // When the background scale reaches 100% update `bgFilled` to make the border
  // bright.
  useAnimatedReaction(
    () => bgScaleSv.get(),
    (currentValue, previousValue) => {
      if (currentValue < 1 && (previousValue === null || previousValue >= 1)) {
        scheduleOnRN(setBgFilled, false);
      } else if (
        currentValue >= 1 &&
        (previousValue === null || previousValue < 1)
      ) {
        scheduleOnRN(setBgFilled, true);
      }
    },
    [bgScaleSv],
  );

  const bgAnimatedStyle = useAnimatedStyle(() => ({
    opacity: bgOpacitySv.get(),
    transform: [{ scale: bgScaleSv.get() }],
  }));

  const pressableAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scaleSv.get() }, { rotateZ: rotationSv.get() }],
  }));

  const flat = pressed || disabled;
  const foregroundColor = foregroundColorByState[state];

  const handleWikiModalDismiss = () => {
    setShowWikiModal(false);
  };

  return (
    <ScopedVariables
      variables={
        foregroundColor == null ? {} : { "--color-fg": foregroundColor }
      }
    >
      <ReanimatedPressable
        {...pressableProps}
        disabled={disabled}
        onHoverIn={(e) => {
          setHovered(true);
          pressableProps.onHoverIn?.(e);
        }}
        onHoverOut={(e) => {
          setHovered(false);
          pressableProps.onHoverOut?.(e);
        }}
        onPressIn={(e) => {
          setPressed(true);
          hapticImpactIfMobile();
          pressableProps.onPressIn?.(e);
        }}
        onPressOut={(e) => {
          setPressed(false);
          pressableProps.onPressOut?.(e);
        }}
        onPress={(e) => {
          if (renderWikiModal) {
            setShowWikiModal(true);
          } else if (state === `error`) {
            // Shake the button violently if the user presses it again after they
            // already made an error.
            rotationSv.set(withIncorrectShakeAnimation());
          } else {
            pressableProps.onPress?.(e);
          }
        }}
        style={pressableAnimatedStyle}
        className={pressableClass({ inFlexRowParent, className })}
      >
        <Reanimated.View
          style={bgAnimatedStyle}
          className={bgAnimatedClass({ state })}
        />
        <View
          className={rectClass({
            flat,
            pressed,
            disabled,
            filled: state !== `default` && bgFilled,
            state,
            hovered,
            className,
          })}
        >
          <Text
            className={textClass({
              state,
              fontSize,
              hasWikiModal: renderWikiModal != null,
              className: textClassName,
              hovered,
            })}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {text}
          </Text>
        </View>
        <ShootingStars
          className="pointer-events-none absolute -inset-3"
          play={state === `success`}
        />
        {showWikiModal && renderWikiModal != null
          ? renderWikiModal(handleWikiModalDismiss)
          : null}
      </ReanimatedPressable>
    </ScopedVariables>
  );
}

const foregroundColorByState: Partial<Record<TextAnswerButtonState, string>> = {
  success: `var(--color-success)`,
  error: `var(--color-danger)`,
  warning: `var(--color-warning)`,
};

const withPulseSpringAnimation = (target: number) =>
  withSequence(
    withTiming(target * 0.5, { duration: 0 }),
    withTiming(target * 1.03, {
      duration: 2 * quizAnimationDuration,
      easing: Easing.inOut(Easing.quad),
    }),
    withTiming(target, {
      duration: quizAnimationDuration,
      easing: Easing.out(Easing.quad),
    }),
  );

const bgAnimatedClass = tv({
  base: `pointer-events-none absolute inset-x-px inset-y-[2px] rounded-lg`,
  variants: {
    state: {
      default: ``,
      dimmed: ``,
      selected: `bg-cyanold/10`,
      success: `bg-fg/10`,
      error: `bg-transparent`,
      warning: `bg-fg/10`,
    },
  },
});

const pressableClass = tv({
  base: `
    focus-visible:rounded-lg focus-visible:outline-4 focus-visible:outline-offset-2
    focus-visible:outline-fg/75

    web:transition-[outline-width]
  `,
  variants: {
    inFlexRowParent: {
      true: `flex-row`,
    },
  },
});

const rectClass = tv({
  base: `items-center justify-center rounded-lg border-2 px-3 py-1 select-none`,
  variants: {
    disabled: {
      true: `cursor-default opacity-50 select-none`,
    },
    flat: {
      true: `mt-[2px]`,
      false: `border-b-4`,
    },
    filled: {
      true: ``,
    },
    pressed: {
      true: ``,
    },
    hovered: {
      true: ``,
    },
    state: {
      default: ``,
      dimmed: ``,
      selected: ``,
      success: ``,
      error: ``,
      warning: ``,
    },
  },
  compoundVariants: [
    {
      filled: false,
      pressed: true,
      class: `border-cyanold/50`,
    },
    // unfilled border
    {
      filled: false,
      class: `border-fg/20`,
    },
    {
      filled: false,
      hovered: true,
      class: `border-fg/30`,
    },
    // filled border
    {
      state: `success`,
      filled: true,
      class: `border-fg`,
    },
    {
      state: `selected`,
      filled: true,
      class: `border-cyanold/90`,
    },
    {
      state: `error`,
      filled: true,
      class: `border-brick`,
    },
    {
      state: `warning`,
      filled: true,
      class: `border-fg`,
    },
  ],
});

const textClass = tv({
  // px-1: Horizontal padding is necessary to give first and last letters on a
  // line with accents enough space to not be clipped. Without this words like
  // "lǐ" will have half the accent clipped.
  //
  // It's important to have decoration-transparent otherwise when the decoration
  // animates in it will start as white and look jarring.
  base: `
    px-1 py-[2px] text-center font-sans font-normal decoration-transparent

    web:transition-colors
  `,
  variants: {
    hovered: {
      true: ``,
    },
    hasWikiModal: {
      true: `underline decoration-currentColor/25 [text-decoration-skip-ink:none]`,
    },
    state: {
      default: `text-fg-loud`,
      dimmed: `text-fg`,
      selected: `text-cyanold`,
      success: `text-fg`,
      error: `text-brick`,
      warning: `text-fg`,
    },
    fontSize: {
      xl: `
        text-xl/tight pyly-ref-xl

        lg:text-2xl/tight lg:pyly-ref-2xl
      `,
      lg: `
        text-lg/tight pyly-ref-lg

        lg:text-xl/tight lg:pyly-ref-xl
      `,
      sm: `
        text-sm pyly-ref-sm

        lg:text-lg/tight lg:pyly-ref-lg
      `,
      xs: `
        text-xs pyly-ref-xs

        lg:text-base/tight lg:pyly-ref-base
      `,
    },
  },
  compoundVariants: [
    {
      hasWikiModal: true,
      hovered: true,
      class: `decoration-currentColor/50`,
    },
  ],
});
